import { useCallback, useEffect, useState } from 'react';
import type { EmbeddingModelId, KnowledgeBase, RetrievedChunk } from '@orqforge/shared';
import { apiClient } from '../lib/api-client';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/Tabs';

const EMBEDDING_MODELS = [
  'openai:text-embedding-3-small',
  'openai:text-embedding-ada-002',
] as const;

function errorMessage(err: unknown): string {
  if (!(err instanceof Error)) return 'Request failed';
  try {
    const parsed = JSON.parse(err.message) as { message?: string | string[] };
    if (typeof parsed.message === 'string') return parsed.message;
    if (Array.isArray(parsed.message)) return parsed.message.join(', ');
  } catch {
    return err.message;
  }
  return err.message;
}

export function KnowledgePage() {
  const [bases, setBases] = useState<KnowledgeBase[]>([]);
  const [selected, setSelected] = useState<KnowledgeBase | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);
  const [kbName, setKbName] = useState('');
  const [kbDesc, setKbDesc] = useState('');
  const [kbModel, setKbModel] = useState<EmbeddingModelId>(EMBEDDING_MODELS[0]);
  const [kbChunkSize, setKbChunkSize] = useState(1000);
  const [kbChunkOverlap, setKbChunkOverlap] = useState(150);
  const [docTitle, setDocTitle] = useState('');
  const [docSource, setDocSource] = useState('');
  const [docContent, setDocContent] = useState('');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<RetrievedChunk[]>([]);
  const [error, setError] = useState<string | null>(null);

  const loadBases = useCallback(async () => {
    setBases(await apiClient.listKnowledgeBases());
  }, []);

  useEffect(() => {
    void loadBases().catch((err: unknown) => setError(errorMessage(err)));
  }, [loadBases]);

  function select(kb: KnowledgeBase): void {
    setSelected(kb);
    setResults([]);
    setSearched(false);
    setQuery('');
  }

  function resetCreate(): void {
    setShowCreate(false);
    setKbName('');
    setKbDesc('');
    setDocTitle('');
    setDocSource('');
    setDocContent('');
    setShowAdvanced(false);
  }

  async function createBase(): Promise<void> {
    if (creating || !kbName.trim()) return;
    setCreating(true);
    setError(null);
    try {
      const kb = await apiClient.createKnowledgeBase({
        name: kbName.trim(),
        description: kbDesc.trim(),
        embeddingModel: kbModel,
        chunkSize: kbChunkSize,
        chunkOverlap: kbChunkOverlap,
      });
      if (docTitle.trim() && docContent.trim()) {
        try {
          await apiClient.ingestDocument(kb.id, {
            title: docTitle.trim(),
            content: docContent,
            sourceUri: docSource.trim() || undefined,
            mimeType: 'text/plain',
          });
        } catch (err) {
          setError(errorMessage(err));
        }
      }
      setCreating(false);
      resetCreate();
      await loadBases();
      select(kb);
    } catch (err) {
      setCreating(false);
      setError(errorMessage(err));
    }
  }

  async function deleteBase(kb: KnowledgeBase): Promise<void> {
    if (deleting) return;
    setDeleting(kb.id);
    setError(null);
    try {
      await apiClient.deleteKnowledgeBase(kb.id);
      if (selected?.id === kb.id) {
        setSelected(null);
        setResults([]);
        setSearched(false);
      }
      await loadBases();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setDeleting(null);
    }
  }

  async function retrieve(): Promise<void> {
    if (!selected || !query.trim() || searching) return;
    setSearching(true);
    setError(null);
    try {
      const rows = await apiClient.retrieveKnowledge(selected.id, { query: query.trim(), topK: 6 });
      setResults(rows);
      setSearched(true);
    } catch (err) {
      setSearched(true);
      setError(errorMessage(err));
    } finally {
      setSearching(false);
    }
  }

  return (
    <div>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Knowledge & Training</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Train agents manually by curating RAG knowledge bases. Create a base, ingest documents
            (chunked and embedded into pgvector), and test retrieval. Automatic, eval-gated prompt and
            rule evolution lives on the Evolution page.
          </p>
        </div>
        <Button size="sm" onClick={() => setShowCreate((open) => !open)}>New knowledge base</Button>
      </div>

      {error ? <p className="mt-3 text-sm text-red-600 dark:text-red-400">{error}</p> : null}

      {showCreate ? (
        <Card className="mt-4 p-4">
          <h2 className="text-base font-semibold text-gray-900 dark:text-white">Create knowledge base</h2>
          <div className="mt-4 flex max-w-xl flex-col gap-1.5">
            <span className="text-xs font-medium">Name</span>
            <span className="text-xs text-gray-500">A short, recognisable name for this base.</span>
            <Input value={kbName} placeholder="e.g. Support playbooks" onChange={(e) => setKbName(e.target.value)} />
          </div>
          <div className="mt-4 flex max-w-xl flex-col gap-1.5">
            <div className="flex items-end justify-between">
              <span className="text-xs font-medium">Description</span>
              <span className={`text-xs tabular-nums ${kbDesc.length > 2000 ? 'text-amber-600' : 'text-gray-500'}`}>
                {kbDesc.length} / 2,000
              </span>
            </div>
            <span className="text-xs text-gray-500">
              Shown to agents at retrieval time. Write with the agent as the audience.
            </span>
            <textarea
              className="h-48 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm leading-relaxed text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
              value={kbDesc}
              placeholder="Describe what this knowledge base covers, its purpose, and any conventions agents should follow when using it."
              onChange={(e) => setKbDesc(e.target.value)}
            />
          </div>

          <div className="my-4 text-xs text-gray-400">Documents</div>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-medium">Document title</span>
              <Input value={docTitle} placeholder="e.g. Password reset SOP" onChange={(e) => setDocTitle(e.target.value)} />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-medium">Source URI</span>
              <Input value={docSource} placeholder="https://... (optional)" onChange={(e) => setDocSource(e.target.value)} />
            </label>
          </div>
          <label className="mt-3 flex flex-col gap-1.5">
            <span className="text-xs font-medium">Content</span>
            <textarea
              className="h-36 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm leading-relaxed text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
              value={docContent}
              placeholder="Paste the document text to chunk, embed, and store in this knowledge base."
              onChange={(e) => setDocContent(e.target.value)}
            />
          </label>

          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="mt-3 w-full justify-between"
            onClick={() => setShowAdvanced((open) => !open)}
          >
            Advanced
          </Button>
          {showAdvanced ? (
            <div className="mt-3 grid gap-4 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <span className="text-xs font-medium">Embedding model</span>
                <Tabs value={kbModel} onValueChange={(value) => setKbModel(value as EmbeddingModelId)}>
                  <TabsList className="flex h-auto flex-wrap">
                    {EMBEDDING_MODELS.map((model) => (
                      <TabsTrigger key={model} value={model}>{model.replace('openai:', '')}</TabsTrigger>
                    ))}
                  </TabsList>
                </Tabs>
              </div>
              <div className="hidden sm:block" />
              <label className="flex flex-col gap-1.5">
                <span className="text-xs font-medium">Chunk size</span>
                <Input
                  type="number"
                  min={100}
                  max={4000}
                  step={50}
                  value={kbChunkSize}
                  onChange={(e) => setKbChunkSize(Number(e.target.value))}
                />
              </label>
              <label className="flex flex-col gap-1.5">
                <span className="text-xs font-medium">Chunk overlap</span>
                <Input
                  type="number"
                  min={0}
                  max={1000}
                  step={10}
                  value={kbChunkOverlap}
                  onChange={(e) => setKbChunkOverlap(Number(e.target.value))}
                />
              </label>
            </div>
          ) : null}

          <div className="mt-4 flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={resetCreate}>Cancel</Button>
            <Button size="sm" disabled={!kbName.trim() || creating} onClick={() => void createBase()}>
              {creating ? 'Creating…' : 'Create'}
            </Button>
          </div>
        </Card>
      ) : null}

      <div className="mt-6 grid grid-cols-[repeat(auto-fill,minmax(260px,1fr))] gap-3">
        {bases.length === 0 ? (
          <div className="col-span-full py-12 text-center">
            <p className="text-sm font-medium text-gray-500">No knowledge bases yet</p>
            <p className="mt-1 text-xs text-gray-400">
              Create one above to start curating documents and training your agents.
            </p>
          </div>
        ) : null}
        {bases.map((kb) => (
          <Card
            key={kb.id}
            className={`cursor-pointer p-4 text-left ${selected?.id === kb.id ? 'ring-2 ring-blue-600' : ''}`}
            onClick={() => select(kb)}
          >
            <div className="flex items-start gap-2">
              <div className="min-w-0 flex-1">
                <strong className="block truncate text-gray-900 dark:text-white">{kb.name}</strong>
                <p className="mt-1 line-clamp-2 text-sm leading-relaxed text-gray-500">
                  {kb.description || 'No description'}
                </p>
              </div>
              <Button
                size="sm"
                variant="ghost"
                aria-label="Delete knowledge base"
                disabled={deleting === kb.id}
                onClick={(event) => {
                  event.stopPropagation();
                  void deleteBase(kb);
                }}
              >
                {deleting === kb.id ? '…' : 'Delete'}
              </Button>
            </div>
            <div className="mt-2 flex items-center gap-2 text-xs text-gray-400">
              <Badge variant="secondary">{kb.embeddingModel.replace('openai:', '')}</Badge>
              <span>{kb.chunkSize} / {kb.chunkOverlap} overlap</span>
            </div>
          </Card>
        ))}
      </div>

      {selected ? (
        <Card className="mt-6 p-4">
          <h2 className="text-base font-semibold text-gray-900 dark:text-white">
            Test retrieval
            <span className="ml-2 text-sm font-normal text-gray-500">· {selected.name}</span>
          </h2>
          <p className="mt-1 text-xs text-gray-500">
            Run a similarity search to verify what an agent would retrieve from this base.
          </p>
          <div className="mt-3 flex gap-2">
            <Input
              placeholder="e.g. how do I reset a password?"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void retrieve();
              }}
            />
            <Button size="sm" variant="secondary" disabled={!query.trim() || searching} onClick={() => void retrieve()}>
              {searching ? '…' : 'Search'}
            </Button>
          </div>
          <div className="mt-3 max-h-72 overflow-y-auto">
            {results.map((row, index) => (
              <div key={`${row.documentId}-${index}`} className="mb-2 rounded-lg bg-gray-50 p-3 dark:bg-gray-950">
                <div className="mb-1 flex items-center justify-between">
                  <span className="text-xs font-medium">{row.documentTitle}</span>
                  <Badge variant="secondary">score {row.score.toFixed(3)}</Badge>
                </div>
                <p className="line-clamp-4 text-xs text-gray-600 dark:text-gray-300">{row.content}</p>
              </div>
            ))}
            {searched && results.length === 0 ? <p className="text-sm text-gray-500">No matches.</p> : null}
          </div>
        </Card>
      ) : null}
    </div>
  );
}
